import React from 'react'
import { isAfter, isBefore, isEqual as isDateEqual } from 'date-fns'
import { concat, sortBy, isEqual as isArrayEqual } from 'lodash'
import { parseDate } from 'utils/utilities'

/* FORM VALIDATION */

type CompareArrayOptions = {
  sortCompare?: boolean
  comparisonMode?: 'normal' | 'with-all'
}

/**
 * Compares two objects to see if they are equal
 * @param object
 * @param other
 * @param fieldsToCompare Narrows down the comparison to specified fields only
 * @param options
 * @returns true if equal, false otherwise
 */
export function compareObjectValues<T = {}>(
  object: T,
  other: T,
  fieldsToCompare?: (keyof T)[],
  options?: CompareArrayOptions
) {
  if (fieldsToCompare) {
    return fieldsToCompare.every((field) => {
      const objectValue = object[field]
      const otherValue = other[field]

      if (Array.isArray(objectValue) && Array.isArray(otherValue)) {
        return compareArrays(objectValue, otherValue, options)
      } else if (typeof objectValue === 'object' && typeof otherValue === 'object') {
        return JSON.stringify(objectValue) === JSON.stringify(otherValue)
      } else {
        return objectValue === otherValue
      }
    })
  }

  return JSON.stringify(object) === JSON.stringify(other)
}

/**
 * Sorts and compares two arrays to see if they are equal
 * @param array
 * @param other
 * @param options
 * @returns true if equal, false otherwise
 */
export const compareArrays = (array: any[], other: any[], options?: CompareArrayOptions) => {
  const { sortCompare = true, comparisonMode = 'with-all' } = options || {}

  const arrayValue = sortCompare ? sortBy(array) : array
  const otherValue = sortCompare ? sortBy(other) : other

  if (comparisonMode === 'with-all') {
    return isArrayEqual(arrayValue, otherValue) || (arrayValue.includes('all') && otherValue.includes('all'))
  }

  return isArrayEqual(arrayValue, otherValue)
}

/**
 * Compares two date strings together
 * @param date Date being compared
 * @param other Date to compare
 * @returns "equal" | "before" | "after" | "invalid"
 */
export const compareDateStrings = (date: string, other: string) => {
  const dateValue = parseDate(date)
  const otherValue = parseDate(other)

  if (dateValue.getDate() && otherValue.getDate()) {
    if (isDateEqual(dateValue, otherValue)) {
      return 'equal'
    } else if (isBefore(dateValue, otherValue)) {
      return 'before'
    } else if (isAfter(dateValue, otherValue)) {
      return 'after'
    }
  }

  return 'invalid'
}

type Fields<T> = T extends string ? T : keyof T
/**
 * Custom hook to create from validation logics
 * @param initialState
 * @returns
 */
export function useValidateFields<T = any>(initialState?: T) {
  type ValidationLogic = {
    onlyFor?: Fields<T>[]
    excluding?: Fields<T>[]
    condition: boolean
    errorText?: string
  }[]
  type FieldLogic = { field: Fields<T>; logic: ValidationLogic }

  const [fieldErrors, setFieldErrors] = React.useState<Fields<T>[]>([])

  /**
   * @param logic The custom logic to set
   * @returns The set validation logic function
   * @example const validationLogic = setValidationLogic((f) => [
   * { condition: !requestForm[f].length },
   * { onlyFor: 'remarks', condition: requestForm[f].length < 10 },
   * { excluding: ['remarks'], condition: requestForm[f].length > 20, errorText: 'Cannot be longer than 20!' }
   * ])
   */
  function setValidationLogic(logic: (fields: Fields<T>) => ValidationLogic) {
    return <F extends Fields<T>>(field: F) => ({ field, logic: logic(field) })
  }

  /**
   * @param fieldLogic Field logic function created by 'setValidationLogic'
   * @param type Optional, 'trigger' = if invalid trigger error state, 'fallthrough' = check if valid even if not triggered
   * @returns True or string if invalid, false if valid
   * @example validateField(validationLogic('companyName'))
   * @example validateField(validationLogic('companyName'), 'trigger')
   * @example validateField(validationLogic('companyName'), 'fallthrough')
   */
  const validateField = (fieldLogic: FieldLogic, type?: 'fallthrough' | 'trigger') => {
    const selectedField = fieldLogic.field
    const selectedFieldLogic = fieldLogic.logic.filter(
      (item) => (!item.onlyFor || item.onlyFor.includes(selectedField)) && !item.excluding?.includes(selectedField)
    )
    const errorField = selectedFieldLogic?.find((item) => item.condition === true)

    if (selectedField && errorField) {
      if (fieldErrors.includes(selectedField) || type === 'fallthrough') return errorField.errorText ?? true
      else if (type === 'trigger') setFieldErrors(concat(fieldErrors, selectedField))
    }

    return false
  }

  /**
   * @param fieldLogic Field logic function created by 'setValidationLogic'
   * @returns Returns errorText if field invalid, otherwise empty string
   * @example getFieldErrorText(validationLogic('companyName'))
   */
  const getFieldErrorText = (fieldLogic: FieldLogic, type?: 'fallthrough') => {
    const value = !type ? validateField(fieldLogic) : validateField(fieldLogic, type)
    if (typeof value === 'string') return value
    return ''
  }

  /**
   * Resets all triggered validations
   */
  const resetValidations = () => {
    setFieldErrors([])
  }

  return {
    setValidationLogic,
    validateField,
    getFieldErrorText,
    resetValidations,
  }
}
